import { Box, Typography, CircularProgress } from '@mui/material';
import { Comment, PDFComment, URLComment } from './Comment';
import { FinishButton, ChoiceButtons } from './Button';
import { Notification } from './NotificationList.types';
import EmptyNotificationMessage from './EmptyNotificationMessage';

const groupByDate = (notifications: Notification[]) => {
  return notifications.reduce((groups: Record<string, Notification[]>, notification: Notification) => {
    const date = new Date(notification.createdAt).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(notification);
    return groups;
  }, {});
};

const renderNotificationContent = (notification: Notification) => {
  switch (notification.notificationType) {
    case 'pdf':
      return (
        <PDFComment
          PDFUrl={notification.actionValue}
          PDFTitle={notification.message}
          PDFSize={notification.altText}
          isRead={notification.isRead}
        />
      );
    case 'url':
      return <URLComment url={notification.actionValue} urlTitle={notification.message} isRead={notification.isRead} />;
    default:
      return <Comment message={notification.message} isRead={notification.isRead} />;
  }
};

const renderNotificationButton = (notification: Notification) => {
  switch (notification.buttonType) {
    case 'finishButton':
      return (
        <FinishButton
          choices={notification.choices || []}
          actionType={notification.actionType || ''}
          actionValue={notification.actionValue || []}
        />
      );
    case 'choiceButtons':
      return (
        <ChoiceButtons
          choices={notification.choices || []}
          actionType={notification.actionType || ''}
          actionValue={notification.actionValue || []}
        />
      );
    default:
      return null;
  }
};

export const NotificationList: React.FC<{ notifications: Notification[] | undefined; isLoading: boolean }> = ({
  notifications,
  isLoading
}) => {
  if (isLoading) {
    // データフェッチ中のスピナーを表示
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!notifications || notifications.length === 0) {
    // 通知が0件の場合に表示
    return <EmptyNotificationMessage />;
  }

  const groupedNotifications = groupByDate(notifications);

  return (
    <>
      {Object.keys(groupedNotifications).map((date) => (
        <Box key={date}>
          <Typography
            align="center"
            sx={{
              fontSize: '0.875rem',
              whiteSpace: 'pre-line',
              margin: 0
            }}
          >
            {date}
          </Typography>
          {groupedNotifications[date].map((notification, index) => (
            <Box key={`${notification.id}-${index}`}>
              {renderNotificationContent(notification)}
              {renderNotificationButton(notification)}
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};
